import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { PopularBrands as PopularBrandsModel } from '@/api/models';
import { PopularBrandsSkeleton } from './PopularBrandsSkeleton';
import { PopularBrandsView } from './PopularBrandsView';
import { usePopularBrands } from './usePopularBrands';

type PopularBrandsContentProps = {
    data: PopularBrandsModel | null;
    error: ErrorResponse | null;
    isLoading: boolean;
};

/**
 * Self-served popular-brands widget — same container / view / skeleton /
 * hook split as the other Sales widgets. `usePopularBrands` reads the live
 * endpoint through `useTopBrandsQuery`.
 */
export const PopularBrands: React.FC = () => {
    const { data, error, isLoading, onRetry } = usePopularBrands();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.popularBrands.errorTitle'),
            errorMessage: t('sales.widgets.popularBrands.errorMessage'),
            errorRetry: t('sales.widgets.popularBrands.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="popular-brands-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="popular-brands-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <PopularBrandsContent
                data={data}
                error={error}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

const PopularBrandsContent: React.FC<PopularBrandsContentProps> = ({
    data,
    error,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <PopularBrandsSkeleton />;
    }

    return <PopularBrandsView data={data} />;
};
